import { useEffect, useState } from 'react';
import { api } from './api.ts';

export interface Company {
  name: string;
  tagline: string;
  hasLogo: boolean;
  /** When the mark was last replaced, used to bust the browser's cache. */
  logoAt: string | null;
}

/** What to show before the real thing arrives, and if it never does. */
const FALLBACK: Company = {
  name: 'Opal', tagline: 'Corrugated Boxes', hasLogo: false, logoAt: null,
};

/**
 * Who this system belongs to.
 *
 * Shown in three places that must agree -- the sidebar, the sign-in screen
 * and the printed costing -- so it is fetched once per page load and shared,
 * exactly as the option lists are. It is also the only fetch that happens
 * before signing in, because the name on the door is how somebody checks they
 * are at the right door.
 */
let cached: Promise<Company> | null = null;

/** Everything on screen showing the name or the mark. */
const watchers = new Set<(c: Company) => void>();

export function loadCompany(): Promise<Company> {
  // Wrapped rather than chained off the call: a client that throws instead of
  // rejecting would skip the catch entirely, and the name on the door is not
  // worth a blank screen.
  cached ??= (async () => {
    try {
      return (await api.get<{ company: Company }>('/api/company')).company ?? FALLBACK;
    } catch {
      return FALLBACK;
    }
  })();
  return cached;
}

/**
 * Drop the cache after Settings changes it, and tell everyone showing it.
 *
 * Clearing the cache alone is not enough: the sidebar resolved its copy when
 * the page loaded and has no reason to ask again, so renaming the company
 * left the old name beside the new one until somebody reloaded.
 */
export function invalidateCompany(): void {
  cached = null;
  void loadCompany().then((c) => { watchers.forEach((notify) => notify(c)); });
}

/**
 * The mark's URL, with the time it changed on it.
 *
 * Without that, replacing the logo leaves the old one on screen until
 * somebody clears their cache -- the URL has not changed, so the browser sees
 * no reason to ask again.
 */
export const logoUrl = (c: Company): string =>
  `/api/company/logo${c.logoAt ? `?v=${encodeURIComponent(c.logoAt)}` : ''}`;

/**
 * Initials, for before there is a logo.
 *
 * "Opal Print & Pack" -> "OP", and a one-word name takes its first two
 * letters rather than one: a single character in a 34px tile reads as a
 * mistake, and "Opal" is exactly the case this has to cover.
 */
export const initialsOf = (name: string): string => {
  const words = name.trim().split(/\s+/).filter(Boolean);
  if (words.length === 0) return 'O';
  if (words.length === 1) return words[0]!.slice(0, 2).toUpperCase();
  return words.slice(0, 2).map((w) => w[0]!.toUpperCase()).join('');
};

export function useCompany(): Company {
  const [company, setCompany] = useState<Company>(FALLBACK);
  useEffect(() => {
    let alive = true;
    const notify = (c: Company) => { if (alive) setCompany(c); };
    watchers.add(notify);
    void loadCompany().then(notify);
    return () => { alive = false; watchers.delete(notify); };
  }, []);
  return company;
}
